import React from 'react';
import type { Monitor } from '../../../types/Monitor';
import { formatPrice, formatPpi, formatResolution, formatDiagonal, formatRefreshRate } from '../../../utils/formatters';
import styles from './MonitorCard.module.css';

interface MonitorCardProps {
  monitor: Monitor;
  onOpenPreview?: (monitor: Monitor) => void;
}

type ResolutionTier = 'fhd' | 'qhd' | '4k' | '5k' | 'other';

function getResolutionTier(h: number): ResolutionTier {
  if (h >= 2880) return '5k';
  if (h >= 2160) return '4k';
  if (h >= 1440) return 'qhd';
  if (h >= 1080) return 'fhd';
  return 'other';
}

const RESOLUTION_LABELS: Record<ResolutionTier, string> = {
  '5k':    '5K',
  '4k':    '4K',
  'qhd':   'QHD',
  'fhd':   'FHD',
  'other': 'HD',
};

export function MonitorCard({ monitor, onOpenPreview }: MonitorCardProps) {
  const {
    brand,
    name,
    diagonalInches,
    nativeResolutionW,
    nativeResolutionH,
    ppi,
    panelType,
    refreshRateHz,
    hdr,
    priceUSD,
  } = monitor;

  const tier = getResolutionTier(nativeResolutionH);

  return (
    <article className={styles.card}>
      {/* ── Header ── */}
      <div className={styles.header}>
        {/* Thumbnail */}
        <div className={styles.thumbnail} aria-hidden="true">
          {monitor.imageUrl ? (
            <img src={monitor.imageUrl} alt="" className={styles.thumbnailImg} />
          ) : (
            <svg width="32" height="28" viewBox="0 0 32 28" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="30" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 21v4M22 21v4M7 25h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </div>
        {/* Text */}
        <div className={styles.headerText}>
          <div className={styles.chips}>
            <span className={`${styles.resolutionChip} ${styles[`chip_${tier}`]}`}>
              {RESOLUTION_LABELS[tier]}
            </span>
            {hdr && <span className={styles.hdrChip}>HDR</span>}
          </div>
          <span className={styles.brand}>{brand}</span>
          <h2 className={styles.name}>{name}</h2>
        </div>
      </div>

      {/* ── Spec grid ── */}
      <dl className={styles.specs}>
        <div className={styles.specItem}>
          <dt className={styles.specLabel}>Size</dt>
          <dd className={styles.specValue}>{formatDiagonal(diagonalInches)}</dd>
        </div>
        <div className={styles.specItem}>
          <dt className={styles.specLabel}>Resolution</dt>
          <dd className={styles.specValue}>{formatResolution(nativeResolutionW, nativeResolutionH)}</dd>
        </div>
        <div className={styles.specItem}>
          <dt className={styles.specLabel}>Pixel Density</dt>
          <dd className={styles.specValue}>{formatPpi(ppi)}</dd>
        </div>
        <div className={styles.specItem}>
          <dt className={styles.specLabel}>Panel</dt>
          <dd className={styles.specValue}>{panelType}</dd>
        </div>
        <div className={styles.specItem}>
          <dt className={styles.specLabel}>Refresh Rate</dt>
          <dd className={styles.specValue}>{formatRefreshRate(refreshRateHz)}</dd>
        </div>
      </dl>

      {/* ── Footer ── */}
      <div className={styles.footer}>
        <span className={styles.price}>{formatPrice(priceUSD)}</span>
        <button
          className={styles.previewBtn}
          onClick={() => onOpenPreview?.(monitor)}
          aria-label={`Open real-scale preview for ${brand} ${name}`}
        >
          Preview
        </button>
      </div>
    </article>
  );
}
